import * as Sentry from '@sentry/react'
import type { ConfirmationLinkSession } from '~/entities/session/lib/confirmation-link'
import * as m from '~/paraglide/messages'
import { storedPartnerCode } from '~/shared/lib/partner-attribution'
import { api, unwrap } from '~/shared/lib/rpc'
import { supabase } from '~/shared/lib/supabase'
import { absoluteUrl } from '~/shared/lib/url'
import type { BusinessUser, LoginInput, RegisterInput } from '~/shared/types'
import type { InviteBusinessInput, InviteBusinessResponse } from '~/shared/types/admin'

export const authApi = {
  async login(credentials: LoginInput): Promise<void> {
    const { error } = await supabase.auth.signInWithPassword({
      email: credentials.email,
      password: credentials.password,
    })
    if (error) throw error
  },

  async adoptSession(session: ConfirmationLinkSession): Promise<void> {
    const { error } = await supabase.auth.setSession({
      access_token: session.accessToken,
      refresh_token: session.refreshToken,
    })
    if (error) throw error
  },

  async register(data: RegisterInput): Promise<{ upgraded: boolean; businessId: string | null }> {
    const res = await api.api.auth.register.business.$post({
      json: {
        email: data.email,
        password: data.password,
        emailRedirectTo: `${absoluteUrl('/login')}?email=${encodeURIComponent(data.email)}`,
        partnerCode: storedPartnerCode() ?? undefined,
      },
    })
    const body = await unwrap<{
      requiresEmailVerification: boolean
      upgraded: boolean
      businessId: string | null
    }>(res)
    return { upgraded: body.upgraded, businessId: body.businessId }
  },

  /**
   * Backoffice flow: create a business for its owner. A fresh email gets a
   * set-password invite; an email that already has an account gets the business
   * attached in place plus a "sign in with your existing password" notice.
   * Backed by POST /api/auth/register/business/invite (global_admin /
   * sales_head / sales_rep). `notificationSent` is false when the business was
   * created but the owner's mail could not be sent.
   */
  async inviteBusinessOwner(data: InviteBusinessInput): Promise<InviteBusinessResponse> {
    const res = await api.api.auth.register.business.invite.$post({
      json: {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        companyName: data.companyName,
        legalName: data.legalName,
        registrationNumber: data.registrationNumber || null,
        emailRedirectTo: `${absoluteUrl('/setup-account')}?role=business`,
      },
    })
    const body = await unwrap<{
      businessId: string
      existingAccount: boolean
      notificationSent: boolean
    }>(res)
    return {
      businessId: body.businessId,
      existingAccount: body.existingAccount,
      notificationSent: body.notificationSent,
    }
  },

  /**
   * Upgrade the currently signed-in personal account to a business account.
   * The bearer session IS the identity - no email/password is sent; the
   * profile and company details follow in the setup.
   */
  async upgradeToBusiness(): Promise<{ businessId: string }> {
    const res = await api.api.auth.register.business.upgrade.$post({
      json: { partnerCode: storedPartnerCode() ?? undefined },
    })
    return unwrap<{ businessId: string }>(res)
  },

  // Through the backend, not GoTrue directly: GoTrue refuses to re-confirm a
  // confirmed address, and the backend answers that case with the
  // account-exists notice pointing at this dashboard.
  async resendVerification(email: string): Promise<void> {
    const res = await api.api.auth['resend-confirmation'].$post({
      json: {
        email,
        surface: 'business',
        emailRedirectTo: `${absoluteUrl('/login')}?email=${encodeURIComponent(email)}`,
      },
    })
    await unwrap<{ sent: true }>(res)
  },

  async checkVerificationStatus(): Promise<{ isVerified: boolean }> {
    const { data } = await supabase.auth.getUser()
    return { isVerified: !!data.user?.email_confirmed_at }
  },

  async getCurrentUser(): Promise<BusinessUser> {
    const res = await api.api['business-users'].me.$get({ query: {} })
    const body = await unwrap<{
      user: { id: string; email: string; roles: string[] }
      profile: { businessId: string }
    }>(res)
    return {
      userId: body.user.id,
      email: body.user.email,
      businessId: body.profile.businessId,
      roles: body.user.roles.join(','),
    }
  },

  async logout(): Promise<void> {
    await supabase.auth.signOut({ scope: 'local' })
  },

  async sendPasswordResetCode(email: string): Promise<void> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: absoluteUrl('/reset-password'),
    })
    if (error) throw error
  },

  /**
   * Set a new password via a Supabase access_token from an invite /
   * recovery email link's URL fragment. The token IS the proof of
   * identity - backend verifies it via supabaseAdmin.auth.getUser and
   * sets the password through admin.updateUserById. No client-side
   * session is needed.
   *
   * Also clears any lingering auth state on success (e.g. an old
   * normal-login session the user happened to have) so the next
   * action goes through /login fresh.
   */
  async setPasswordWithToken(accessToken: string, newPassword: string): Promise<{ email: string }> {
    const res = await api.api.auth.password.set.$post({
      json: { accessToken, newPassword },
    })
    const body = await unwrap<{ email: string; sessionsRevoked: boolean }>(res)
    // The backend revokes the account's other sessions best-effort and has no
    // error reporting of its own; a false here means the password did change
    // while sessions taken with the old one may still be alive.
    if (!body.sessionsRevoked) {
      Sentry.captureException(new Error('Password changed but other sessions were not revoked'), {
        extra: { module: 'session', action: 'setPasswordWithToken' },
      })
    }
    await supabase.auth.signOut({ scope: 'local' }).catch(() => undefined)
    return { email: body.email }
  },

  async requestAccountDeletion(_email: string): Promise<void> {
    throw new Error(m.accountDeletionNotAvailable())
  },
}
